import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageLimitFilter } from '@typings';
import { useCallback } from 'react';

import { customerApi } from '../api/customerApi';
import { ToastMessage } from '../components/ToastMessage';

export const useFinancialCustomerSubscriptions = (
  customerId: string,
  pagination: PageLimitFilter
) => {
  return useQuery({
    queryKey: ['customer-subscriptions', customerId, pagination],
    queryFn: () =>
      customerApi.fetchFinancialCustomerSubscriptions(customerId, pagination),
  });
};

export const useBillings = (subscriptionId: number) => {
  return useQuery({
    queryKey: ['billings', subscriptionId],
    queryFn: () => customerApi.fetchBilling(subscriptionId),
  });
};

export const useTransactions = (billingId: number) => {
  return useQuery({
    queryKey: ['transactions', billingId],
    queryFn: () => customerApi.fetchTransactions(billingId),
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  const onSuccess = useCallback(
    (_: any, variables: { subscriptionId: number }) => {
      queryClient.invalidateQueries({
        queryKey: ['customer-subscriptions', variables.subscriptionId],
      });
      ToastMessage({
        content: 'Renovação cancelada com sucesso!',
        type: 'success',
      });
    },
    [queryClient]
  );

  const onError = useCallback(() => {
    ToastMessage({
      content: 'Falha ao cancelar renovação. Tente novamente!',
      type: 'error',
    });
  }, [queryClient]);

  const { mutateAsync: cancelSubscription, isPending: isLoading } = useMutation(
    {
      mutationFn: ({
        subscriptionId,
        reasonId,
      }: {
        subscriptionId: number;
        reasonId: number;
      }) => customerApi.cancelSubscription(subscriptionId, reasonId),
      onSuccess,
      onError,
    }
  );

  return { cancelSubscription, isLoading };
};

export const useReactiveSubscription = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: reactiveSubscription } = useMutation({
    mutationFn: (subscriptionId: number) =>
      customerApi.reactiveSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
    },
  });
  return { reactiveSubscription };
};

export const useCancelAutoRescue = () => {
  const queryClient = useQueryClient();

  const onSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['billings'] });
    ToastMessage({
      content: 'Auto Rescue cancelado com sucesso!',
      type: 'success',
    });
  }, [queryClient]);

  const onError = useCallback(() => {
    ToastMessage({
      content: 'Falha ao cancelar Auto Rescue. Tente novamente!',
      type: 'error',
    });
  }, [queryClient]);

  const { mutateAsync: cancelAutoRescue, isPending: isLoading } = useMutation({
    mutationFn: ({
      subscriptionId,
      reasonId,
    }: {
      subscriptionId: number;
      reasonId: number;
    }) => customerApi.cancelAutoRescue(subscriptionId, reasonId),
    onSuccess,
    onError,
  });

  return { cancelAutoRescue, isLoading };
};

export const useRefund = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: refund, isPending: isLoading } = useMutation({
    mutationFn: (refundData: {
      gatewayTransaction: string;
      amount: number;
      billingId: number;
      cancellation: boolean;
      document: string;
      reasonId: number;
    }) => {
      return customerApi.refund(refundData);
    },
    onSuccess: () => {
      ToastMessage({
        content: 'Reembolso realizado com sucesso!',
        type: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['transactions', 'billings'],
      });
    },
    onError: () => {
      ToastMessage({
        content: `Erro ao efetuar o reembolso. Por favor, tente novamente!`,
        type: 'error',
      });
    },
  });

  return { refund, isLoading };
};

export const useReasonOptions = () => {
  return useQuery({
    queryKey: ['reasonOptions'],
    queryFn: () => customerApi.fetchReasonOptions(),
  });
};
